import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import styles from "./MaintenanceBanner.module.css";

/**
 * Persistent app-shell banner shown when website registration/login is in
 * maintenance mode (specs/REGISTRATION_MAINTENANCE_MODE.md, Step 7 option (a)).
 *
 * A dumb presentational consumer of FeatureFlagsContext's
 * `registrationMaintenanceMode` and `registrationMaintenanceNotice` — it owns no
 * fetching of its own. That context refreshes its snapshot on a poll interval
 * and whenever the tab becomes visible again, so this banner converges on the
 * real state in both directions (appearing when the window opens, disappearing
 * when it closes) within at most one poll interval, and immediately on a tab the
 * user has just switched back to. If a refresh fails the context keeps its last
 * good snapshot, so a transient BFF error leaves the banner as it was rather
 * than silently hiding an open maintenance window.
 *
 * The notice carries an operator-selected estimated return time and is appended
 * after the standing sentence when set. When it is "" (no estimate, or the flag
 * is unresolved) the banner reads exactly as it does without it — no placeholder
 * and no invented time.
 *
 * Deliberately does not surface who enabled maintenance mode or when — that
 * detail lives in the flag admin's History tab, out of scope here.
 */
export function MaintenanceBanner() {
  const { registrationMaintenanceMode, registrationMaintenanceNotice } = useFeatureFlags();

  if (!registrationMaintenanceMode) {
    return null;
  }

  return (
    <div className={styles.maintenanceBanner} role="status">
      Website registration is in maintenance mode — new registrations and logins are paused.
      {registrationMaintenanceNotice ? ` ${registrationMaintenanceNotice}` : null}
    </div>
  );
}
